import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [activeContext, setActiveContext] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('🔐 UserContext - Auth state changed, user:', !!currentUser);
      setLoading(true); // Ensure we're in loading state while processing
      
      if (currentUser) {
        setUser(currentUser);
        console.log('🔐 UserContext - User set, fetching Firestore document...');

        try {
          // Fetch logged-in user's Firestore document
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const data = userDocSnap.data();
            console.log('🔐 UserContext - User data loaded:', data.role);
            setUserData(data);
            setActiveContext(data); // Default activeContext is the logged-in user's own data
          } else {
            console.log('🔐 UserContext - No user document found in Firestore');
            setUserData(null);
            setActiveContext(null);
          }
        } catch (error) {
          console.error('🔐 UserContext - Error fetching user data:', error);
          setUserData(null);
          setActiveContext(null);
        }
      } else {
        console.log('🔐 UserContext - No user, clearing data');
        setUser(null);
        setUserData(null);
        setActiveContext(null);
      }
      
      setLoading(false);
      console.log('🔐 UserContext - Loading complete');
    });

    return () => unsubscribe();
  }, []);

  // Function to switch the active context
  const switchContext = async (uid) => {
    const userDocRef = doc(db, "users", uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      throw new Error("User document not found.");
    }

    setActiveContext(userDocSnap.data());
  };

  return (
    <UserContext.Provider
      value={{
        user,
        userData,
        activeContext,
        setUserData,
        switchContext,
        loading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
